import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  OBJECT_ELEMENT_CATEGORIES,
  OBJECT_TYPES,
  MODES,
} from "../configuratorConstants";
import elementService from "../../api/elementService";

import { ReactComponent as DoorIcon } from "../../Assets/door.svg";
import { ReactComponent as WindowIcon } from "../../Assets/window.svg";
import { ReactComponent as WindowBlindsIcon } from "../../Assets/roleta.svg";
import { ReactComponent as SocketX1Icon } from "../../Assets/socketx1.svg";
import { ReactComponent as SocketX2Icon } from "../../Assets/socketx2.svg";
import { ReactComponent as LedIcon } from "../../Assets/led.svg";
import { ReactComponent as SwitchX1Icon } from "../../Assets/switchx1.svg";
import { ReactComponent as SwitchX2Icon } from "../../Assets/switchx2.svg";
import { ReactComponent as ToiletIcon } from "../../Assets/toilet.svg";
import { ReactComponent as HandBasinIcon } from "../../Assets/hand_basin.svg";
import { ReactComponent as WaterHeaterIcon } from "../../Assets/water_heater.svg";
import { ReactComponent as HeaterIcon } from "../../Assets/heater.svg";
import { ReactComponent as AirConditionerInternalIcon } from "../../Assets/airin.svg";
import { ReactComponent as AirConditionerExternalIcon } from "../../Assets/airout.svg";
import { ReactComponent as ShowerIcon } from "../../Assets/Душ.svg";
import { ReactComponent as GateIcon } from "../../Assets/Ворота.svg";
import { ViewfinderCircleIcon, PencilIcon } from "@heroicons/react/24/outline";

const PlaceholderIcon = ({ className = "w-6 h-6 text-gray-500" }) => (
  <svg
    className={className}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M4 6h16M4 12h16m-7 6h7"
    />
  </svg>
);

const ICON_MAP = {
  DoorSvg: DoorIcon,
  WindowSvg: WindowIcon,
  WindowBlindsSvg: WindowBlindsIcon,
  SocketX1Svg: SocketX1Icon,
  SocketX2Svg: SocketX2Icon,
  LedSvg: LedIcon,
  SwitchX1Svg: SwitchX1Icon,
  SwitchX2Svg: SwitchX2Icon,
  InternalWallSvg: ViewfinderCircleIcon,
  PencilIcon: PencilIcon,
  ToiletSvg: ToiletIcon,
  HandBasinSvg: HandBasinIcon,
  WaterHeaterSvg: WaterHeaterIcon,
  HeaterSvg: HeaterIcon,
  AirConditionerInternalSvg: AirConditionerInternalIcon,
  AirConditionerExternalSvg: AirConditionerExternalIcon,
  ShowerSvg: ShowerIcon,
  GateSvg: GateIcon,
};

// Маппинг категорий из БД в категории панели
const CATEGORY_MAPPING = {
  structures: 'element_categories.structures',
  doors_windows: 'element_categories.doors_windows',
  electricity: 'element_categories.electricity',
  sanitary: 'element_categories.sanitary',
  climate: 'element_categories.climate',
  furniture: 'element_categories.furniture',
  custom: 'element_categories.custom',
};

const ElementSelectionPanel = ({
  onSelectElementType,
  selectedElementType,
  activeMode,
  framelessElementsUnlocked = false,
  hasRooms = false,
}) => {
  const { t } = useTranslation();
  const [dbElements, setDbElements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadElements = async () => {
      try {
        setLoading(true);
        const response = await elementService.getElementsForConfigurator(activeMode);
        
        // Парсим JSON-поля если они пришли как строки
        const parsedElements = (response.data || []).map(element => {
          const parsed = { ...element };
          
          // Парсим allowedModes если это строка
          if (typeof parsed.allowedModes === 'string') {
            try {
              parsed.allowedModes = JSON.parse(parsed.allowedModes);
            } catch (e) {
              parsed.allowedModes = [];
            }
          }
          
          // Парсим transformRules если это строка
          if (typeof parsed.transformRules === 'string') {
            try {
              parsed.transformRules = JSON.parse(parsed.transformRules);
            } catch (e) {
              parsed.transformRules = null;
            }
          }
          
          return parsed;
        });
        
        setDbElements(parsedElements);
        
        // Логируем для отладки
        if (process.env.NODE_ENV !== "production") {
          console.log('📦 [ElementSelectionPanel] Элементы загружены:', {
            count: parsedElements.length,
            elements: parsedElements.map(el => ({
              code: el.elementCode,
              name: el.elementName,
              category: el.category,
              isActive: el.isActive,
              allowedModes: el.allowedModes
            })),
            activeMode
          });
          
          // Проверяем конкретный элемент
          const testElement = parsedElements.find(el => el.elementCode === 'test213');
          if (testElement) {
            console.log('✅ [ElementSelectionPanel] Элемент test213 найден:', testElement);
          } else {
            console.log('⚠️ [ElementSelectionPanel] Элемент test213 НЕ найден в загруженных элементах');
          }
        }
      } catch (error) {
        console.error('❌ [ElementSelectionPanel] Ошибка при загрузке элементов:', error);
      } finally {
        setLoading(false);
      }
    };

    loadElements();
  }, [activeMode]);

  // Группируем элементы из БД по категориям
  // Для стандартных категорий используем titleKey из CATEGORY_MAPPING
  // Для пользовательских категорий используем реальное имя категории
  const dbElementsByCategory = dbElements.reduce((acc, element) => {
    const categoryKey = CATEGORY_MAPPING[element.category] || element.category;
    if (!acc[categoryKey]) {
      acc[categoryKey] = [];
    }
    acc[categoryKey].push(element);
    return acc;
  }, {});

  // Функция для получения иконки элемента
  const getElementIcon = (element, item) => {
    // Если есть кастомная иконка в БД, используем её
    // Иначе используем SVG файл элемента как иконку
    const iconUrl = element?.iconPath || element?.svgPath;
    
    if (iconUrl) {
      return (
        <img
          src={`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}${iconUrl}`}
          alt={element.elementName}
          className="w-6 h-6 mr-4 flex-shrink-0 object-contain"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      );
    }
    
    // Если есть захардкоженная иконка (для старых элементов)
    if (item?.icon && ICON_MAP[item.icon]) {
      const IconComponent = ICON_MAP[item.icon];
      return <IconComponent className="w-6 h-6 mr-4 flex-shrink-0" />;
    }
    
    // Маппинг иконок по elementCode для элементов из БД
    const iconMapping = {
      'door': 'DoorSvg',
      'gate': 'GateSvg',
      'window': 'WindowSvg',
      'panoramic_window': 'WindowSvg',
      'window_blinds': 'WindowBlindsSvg',
      'outlet': 'SocketX1Svg',
      'outlet_double': 'SocketX2Svg',
      'switch': 'SwitchX1Svg',
      'switch_double': 'SwitchX2Svg',
      'light_ceiling': 'LedSvg',
      'toilet': 'ToiletSvg',
      'hand_basin': 'HandBasinSvg',
      'water_heater': 'WaterHeaterSvg',
      'heater': 'HeaterSvg',
      'air_conditioner': 'AirConditionerInternalSvg',
      'air_conditioner_external': 'AirConditionerExternalSvg',
      'shower': 'ShowerSvg',
      'internal_wall_tool': 'InternalWallSvg',
      'frameless_wall_tool': 'PencilIcon',
    };
    
    const iconName = iconMapping[item?.type];
    if (iconName && ICON_MAP[iconName]) {
      const IconComponent = ICON_MAP[iconName];
      return <IconComponent className="w-6 h-6 mr-4 flex-shrink-0" />;
    }
    
    return <PlaceholderIcon className="w-6 h-6 mr-4 flex-shrink-0" />;
  };

  // Объединяем захардкоженные категории с элементами из БД
  const allCategories = [...OBJECT_ELEMENT_CATEGORIES];
  
  // Добавляем категории из БД, которых нет в захардкоженных
  Object.keys(dbElementsByCategory).forEach(categoryKey => {
    const exists = allCategories.some(cat => cat.titleKey === categoryKey);
    if (!exists) {
      allCategories.push({
        titleKey: categoryKey,
        items: []
      });
    }
  });

  return (
    <div
      id="element-selection-panel"
      className="h-full w-full bg-card-bg-light p-4 overflow-y-auto"
    >
      <h2 className="text-lg font-semibold mb-4 border-b border-gray-200 pb-3 text-gray-800 tracking-wide">
        {t("elementSelectionPanel.title")}
      </h2>
      {allCategories.map((category) => {
        // Получаем элементы из БД для этой категории
        const dbCategoryElements = dbElementsByCategory[category.titleKey] || [];
        
        // Преобразуем элементы из БД в формат панели
        const dbItems = dbCategoryElements.map(element => ({
          type: element.elementCode, // Используем elementCode как тип
          labelKey: element.elementName,
          placement: element.placementType === 'wall' ? OBJECT_TYPES.PLACEMENT_TYPE_WALL : null,
          icon: null, // Будем использовать кастомную иконку
          mode: null, // Не используем mode для элементов из БД - фильтрация идет по allowedModes
          element: element, // Сохраняем полный объект элемента
        }));

        // Фильтруем захардкоженные элементы - оставляем только те, которых НЕТ в БД
        const hardcodedItems = (category.items || []).filter(item => {
          // Проверяем есть ли элемент с таким же типом в БД
          const existsInDb = dbItems.some(dbItem => dbItem.type === item.type);
          return !existsInDb; // Оставляем только если НЕТ в БД
        });

        // Объединяем: сначала элементы из БД, потом оставшиеся захардкоженные
        const allItems = [...dbItems, ...hardcodedItems];
        const filteredItems = allItems.filter((item) => {
          // Скрываем элемент настройки цены за квадратный метр - он только для админ-панели
          if (item.type === OBJECT_TYPES.FRAMELESS_SQUARE_METER_PRICE) {
            return false;
          }
          
          // Для элементов из БД проверяем allowedModes
          if (item.element) {
            const allowedModes = item.element.allowedModes || [];
            
            // Логируем для отладки конкретного элемента
            if (item.type === 'test213' && process.env.NODE_ENV !== "production") {
              console.log('🔍 [ElementSelectionPanel] Фильтрация элемента test213:', {
                type: item.type,
                allowedModes,
                activeMode,
                isAllowed: allowedModes.length === 0 || allowedModes.includes(activeMode),
                category: category.titleKey,
                elementData: item.element
              });
            }
            
            // Если allowedModes пустой массив - элемент доступен во всех режимах
            // Если allowedModes заполнен - проверяем, что текущий режим в списке
            if (allowedModes.length > 0 && !allowedModes.includes(activeMode)) {
              if (item.type === 'test213' && process.env.NODE_ENV !== "production") {
                console.log('❌ [ElementSelectionPanel] Элемент test213 отфильтрован по allowedModes:', {
                  allowedModes,
                  activeMode
                });
              }
              return false;
            }
          }
          
          if (
            item.type === OBJECT_TYPES.INTERNAL_WALL_TOOL &&
            activeMode === MODES.FRAME
          ) {
            item.icon = "InternalWallSvg";
          }
          if (
            item.type === OBJECT_TYPES.FRAMELESS_WALL_TOOL &&
            activeMode === MODES.FRAMELESS
          ) {
            item.icon = "PencilIcon";
          }
          return !item.mode || item.mode === activeMode;
        });

        if (filteredItems.length === 0) {
          return null;
        }

        // Проверяем, все ли элементы в категории отключены в безкаркасном режиме
        const allItemsDisabled =
          activeMode === MODES.FRAMELESS &&
          !framelessElementsUnlocked &&
          filteredItems.every(
            (item) => item.type !== OBJECT_TYPES.FRAMELESS_WALL_TOOL,
          );

        // Определяем, является ли категория стандартной (начинается с 'element_categories.')
        const isStandardCategory = category.titleKey.startsWith('element_categories.');
        const categoryTitle = isStandardCategory ? t(category.titleKey) : category.titleKey;

        return (
          <div key={category.titleKey} className="mb-6">
            <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 ${allItemsDisabled ? "text-gray-400 opacity-40" : "text-gray-500"
              }`}>
              {categoryTitle}
            </h3>
            <div className="flex flex-col gap-3">
              {filteredItems.map((item) => {
                // Для элементов из БД используем кастомную иконку
                const IconComponent = item.element ? null : (ICON_MAP[item.icon] || PlaceholderIcon);
                const isSelected = selectedElementType === item.type;
                const labelText = item.element ? item.element.elementName : t(item.labelKey);

                // В безкаркасном режиме только FRAMELESS_WALL_TOOL активен (для hardcoded элементов)
                // Элементы из БД всегда активны если прошли фильтрацию по allowedModes
                const isFramelessWallTool =
                  item.type === OBJECT_TYPES.FRAMELESS_WALL_TOOL;
                const isDbElement = !!item.element;
                const isInternalWallTool = item.type === OBJECT_TYPES.INTERNAL_WALL_TOOL;
                // Внутренняя стена неактивна, когда нет комнат
                const isInternalWallDisabled = isInternalWallTool && activeMode === MODES.FRAMELESS && !hasRooms;
                const isDisabled =
                  isInternalWallDisabled || // Внутренняя стена неактивна без комнат
                  (activeMode === MODES.FRAMELESS &&
                  !isFramelessWallTool &&
                  !framelessElementsUnlocked &&
                  !isDbElement); // Элементы из БД не отключаем

                let tooltipText;
                if (item.type === OBJECT_TYPES.FRAMELESS_WALL_TOOL) {
                  tooltipText = t("tooltips.select_tool_draw", {
                    label: labelText,
                  });
                } else if (item.type === OBJECT_TYPES.INTERNAL_WALL_TOOL) {
                  tooltipText = t("tooltips.select_tool_wall", {
                    label: labelText,
                  });
                } else {
                  tooltipText = t("tooltips.select_tool", { label: labelText });
                }

                return (
                  <button
                    key={item.type}
                    onClick={() => {
                      if (!isDisabled) {
                        onSelectElementType(item.type);
                      }
                    }}
                    disabled={isDisabled}
                    className={`group w-full flex items-center text-left px-4 py-3 rounded-lg transition-all duration-150 ease-in-out border
                        ${isDisabled
                        ? "bg-gray-100 text-gray-400 border-gray-200 opacity-40 cursor-not-allowed"
                        : isSelected
                          ? "bg-primary-blue text-white shadow-md border-transparent"
                          : "bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-100"
                      }`}
                    title={isDisabled ? "" : tooltipText}
                  >
                    {isDbElement ? (
                      getElementIcon(item.element, item)
                    ) : (
                      <IconComponent
                        className={`w-6 h-6 mr-4 flex-shrink-0 transition-colors ${isDisabled
                          ? "text-gray-400"
                          : isSelected
                            ? "text-white"
                            : "text-primary-blue/70 group-hover:text-primary-blue"
                          }`}
                      />
                    )}
                    <span className="text-base font-medium">{labelText}</span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

ElementSelectionPanel.propTypes = {
  onSelectElementType: PropTypes.func.isRequired,
  selectedElementType: PropTypes.string,
  activeMode: PropTypes.string.isRequired,
  framelessElementsUnlocked: PropTypes.bool,
};

export default ElementSelectionPanel;